import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { fetchLearningResources } from '@/service/api';
import { useLearningCategoryStore, useAssetRenewStore } from '@/stores';
import { BannerCarousel, Pagination, FilterTabs } from '@/components/common';
import heroIllustration from '@/assets/home/learning-course.png';
import logoImg from '@/assets/logo.png';
import '@/styles/fluid-pages.css';

const ITEMS_PER_PAGE = 10;

type ViewMode = 'card' | 'list';

const courseTypes = ['全部', '视频课程', '图文课程'];

const difficultyOptions = [
  { label: '全部', value: 'all' },
  { label: '入门', value: 'BEGINNER' },
  { label: '进阶', value: 'INTERMEDIATE' },
  { label: '高级', value: 'ADVANCED' }
];

const accessModeOptions = [
  { label: '全部', value: 'all' },
  { label: '免费', value: 'FREE' },
  { label: '积分', value: 'POINTS' }
];

const levelLabel: Record<string, string> = {
  BEGINNER: '入门',
  INTERMEDIATE: '进阶',
  ADVANCED: '高级',
  beginner: '入门',
  intermediate: '进阶',
  advanced: '高级',
  '入门': '入门',
  '进阶': '进阶',
  '高级': '高级'
};

function formatDuration(seconds?: number): string {
  if (!seconds) return '暂无时长';
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}分钟`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins > 0 ? `${hours}小时${remainingMins}分` : `${hours}小时`;
}

export default function CoursesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('');
  const { categories, fetchCategories } = useLearningCategoryStore();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('全部');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedAccessMode, setSelectedAccessMode] = useState<string>('all');
  const [selectedSort, setSelectedSort] = useState<string>('default');
  const [isFeatured, setIsFeatured] = useState<boolean>(false);
  const [showAllFilters, setShowAllFilters] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [currentPage, setCurrentPage] = useState(1);

  const hasActiveSecondaryFilters = useMemo(() => {
    return (
      selectedType !== '全部' ||
      selectedDifficulty !== 'all' ||
      selectedAccessMode !== 'all'
    );
  }, [selectedType, selectedDifficulty, selectedAccessMode]);

  const [courses, setCourses] = useState<Api.Learning.ResourceResponse[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  // 防重标识 useRef
  const lastFetchParamsRef = useRef<string | null>(null);

  // 获取资源分类
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);


  // 获取学习资源列表
  const loadResources = useCallback(async (force = false) => {
    const typeParam = selectedType === '视频课程' ? 'VIDEO' : selectedType === '图文课程' ? 'ARTICLE' : undefined;
    const categoryParam = selectedCategoryId === 'all' ? undefined : selectedCategoryId;
    const difficultyParam = selectedDifficulty === 'all' ? undefined : selectedDifficulty;
    const accessModeParam = selectedAccessMode === 'all' ? undefined : selectedAccessMode;

    const queryParams: Api.Learning.GetLearningResourcesParams = {
      category_id: categoryParam,
      resource_type: typeParam,
      difficulty: difficultyParam,
      access_mode: accessModeParam,
      featured: isFeatured ? true : undefined,
      keyword: activeKeyword || undefined,
      sort: selectedSort !== 'default' ? selectedSort : undefined,
      page: currentPage,
      page_size: ITEMS_PER_PAGE
    };

    const paramsKey = JSON.stringify(queryParams);

    // 相同请求参数且非强制刷新时跳过，避免刷新/初始化及 React 18 StrictMode 导致的重复调用
    if (!force && lastFetchParamsRef.current === paramsKey) {
      return;
    }
    lastFetchParamsRef.current = paramsKey;

    setLoading(true);
    try {
      const res = await fetchLearningResources(queryParams);
      setCourses(res?.items || []);
      setTotalCount(res?.total || 0);
    } catch (err) {
      console.error('获取学习资源失败', err);
      setCourses([]);
      setTotalCount(0);
      lastFetchParamsRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [selectedCategoryId, selectedType, selectedDifficulty, selectedAccessMode, selectedSort, isFeatured, activeKeyword, currentPage]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const assetCache = useAssetRenewStore((state) => state.assetCache);

  // 页面离开/离场时自动清空当前页面的临时续签缓存
  useEffect(() => {
    return () => {
      useAssetRenewStore.getState().clearCache();
    };
  }, []);

  // 监听课程列表更新，自动收集课程封面并在必要时向 Store 注册与发起 OSS 资源续签
  useEffect(() => {
    if (!courses.length) return;

    const renewRequests: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    const singleAssetIds: string[] = [];

    courses.forEach((c) => {
      const resourceId = String(c.id || '');
      const coverAssetId = c.cover_asset?.id || (c.cover_asset as any)?.asset_id || (c as any).cover_asset_id || (c as any).asset_id;
      if (!coverAssetId) return;

      const assetIdStr = String(coverAssetId);
      const accessUrl = c.cover_asset?.access_url || (c as any).cover_url;
      const accessExpires = c.cover_asset?.access_expires;

      // 1. 优先将原接口（fetchLearningResources）刚拉取到的新鲜 access_url 预存入 Store 缓存
      if (accessUrl) {
        useAssetRenewStore.getState().setAssetCacheItem(assetIdStr, accessUrl, accessExpires);
      }

      // 2. 收集所有的 LEARNING_LESSON 业务资源上下文（注册入 Store 进行到期自动巡检）
      if (resourceId) {
        renewRequests.push({
          resource_type: 'LEARNING_RESOURCE',
          resource_id: resourceId,
          asset_ids: [assetIdStr],
        });
      } else {
        singleAssetIds.push(assetIdStr);
      }
    });

    if (renewRequests.length) {
      useAssetRenewStore.getState().renewBatchResources(renewRequests).catch(() => { });
    }

    singleAssetIds.forEach((assetId) => {
      useAssetRenewStore.getState().renewSingleAsset(assetId, 'LEARNING_RESOURCE').catch(() => { });
    });
  }, [courses]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setActiveKeyword(searchQuery.trim());
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    // 清空搜索框时自动重置搜索
    if (!val.trim() && activeKeyword) {
      setCurrentPage(1);
      setActiveKeyword('');
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const resetFilters = () => {
    lastFetchParamsRef.current = null;
    setSearchQuery('');
    setActiveKeyword('');
    setSelectedCategoryId('all');
    setSelectedType('全部');
    setSelectedDifficulty('all');
    setSelectedAccessMode('all');
    setSelectedSort('default');
    setIsFeatured(false);
    setCurrentPage(1);
  };

  return (
    <div className="courses-page min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Hero：双 Banner 轮播宽度铺满容器（高度参考 talents 中“成为创作者”按钮） */}
      <div className="relative pt-24 sm:pt-28 pb-2 bg-white overflow-hidden">
        <div className="site-shell max-w-7xl mx-auto px-4 md:px-8 py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full">
            <BannerCarousel
              type="courses"
              aspectRatio="2307/494"
              hoverScale={false}
              className="w-full rounded-2xl overflow-hidden shadow-2xs transition-all duration-300 hover:shadow-md hover:scale-[1.015] cursor-pointer"
            />
            <BannerCarousel
              type="courses_2"
              aspectRatio="2307/494"
              hoverScale={false}
              className="w-full rounded-2xl overflow-hidden shadow-2xs transition-all duration-300 hover:shadow-md hover:scale-[1.015] cursor-pointer"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="top-16 z-30 bg-white backdrop-blur-md pb-3 pt-2">
        <div className="site-shell max-w-7xl px-4 md:px-8 space-y-3">
          {/* 单行 Filter 容器：课程分类 + 排序方式 + 全部筛选 + 复选框 + 视图切换 */}
          <div className="flex items-center gap-5 flex-wrap lg:flex-nowrap">
            {/* 1. 课程分类 */}
            <FilterTabs
              label={<span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap mr-1">课程分类：</span>}
              options={categories.map((cat) => ({
                label: cat.name,
                value: cat.id || cat.code || '',
              }))}
              value={selectedCategoryId}
              onChange={(val) => {
                setSelectedCategoryId(val);
                setCurrentPage(1);
              }}
              allLabel="全部课程"
              allValue="all"
              popoverTitle="全部分类"
              itemClassName="text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer shrink-0 select-none"
              activeItemClassName="bg-[#e6f7ef] text-[#00c9a7] font-semibold"
              inactiveItemClassName="text-gray-600 hover:text-gray-900 hover:bg-gray-100/80"
            />


            {/* 2. 排序方式 */}
            <div className="flex items-center gap-2 overflow-x-auto flex-nowrap shrink-0 py-1">
              <span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap mr-1">排序方式：</span>
              <button
                type="button"
                onClick={() => { setSelectedSort('default'); setCurrentPage(1); }}
                className={`text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer ${selectedSort === 'default'
                  ? 'bg-[#e6f7ef] text-[#00c9a7] font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                  }`}
              >
                综合排序
              </button>

              <button
                type="button"
                onClick={() => { setSelectedSort('latest'); setCurrentPage(1); }}
                className={`text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer ${selectedSort === 'latest'
                  ? 'bg-[#e6f7ef] text-[#00c9a7] font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                  }`}
              >
                最新上架
              </button>

              <button
                type="button"
                onClick={() => { setSelectedSort('popular'); setCurrentPage(1); }}
                className={`text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer ${selectedSort === 'popular'
                  ? 'bg-[#e6f7ef] text-[#00c9a7] font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                  }`}
              >
                最受欢迎
              </button>

              <button
                type="button"
                onClick={() => {
                  const nextSort = selectedSort === 'price_asc' ? 'price_desc' : 'price_asc';
                  setSelectedSort(nextSort);
                  setCurrentPage(1);
                }}
                className="text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100/80"
              >
                <span>价格</span>
                <i className={`text-xs ${selectedSort === 'price_asc'
                  ? 'ri-arrow-up-line'
                  : selectedSort === 'price_desc'
                    ? 'ri-arrow-down-line'
                    : 'ri-arrow-up-down-line'
                  }`}></i>
              </button>
            </div>

            {/* 3. 全部筛选 + 复选框 */}
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setShowAllFilters(!showAllFilters)}
                className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer border ${showAllFilters
                  ? 'bg-emerald-50 text-emerald-600 border-emerald-300 font-medium'
                  : hasActiveSecondaryFilters
                    ? 'bg-emerald-50/60 text-emerald-600 border-emerald-200 font-medium'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <i className="ri-filter-3-line text-xs"></i>
                <span>全部筛选</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${showAllFilters ? 'rotate-180' : ''}`}></i>
              </button>

              <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none hover:text-emerald-600 transition-colors whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={selectedAccessMode === 'FREE'}
                  onChange={(e) => {
                    setSelectedAccessMode(e.target.checked ? 'FREE' : 'all');
                    setCurrentPage(1);
                  }}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer accent-emerald-500"
                />
                <span>仅看免费课</span>
              </label>

              <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none hover:text-emerald-600 transition-colors whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={(e) => {
                    setIsFeatured(e.target.checked);
                    setCurrentPage(1);
                  }}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer accent-emerald-500"
                />
                <span>精品资源</span>
              </label>
              <Link
                to="/courses/favorites"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer border bg-emerald-50/80 text-emerald-600 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 font-medium whitespace-nowrap"
              >
                <i className="ri-bookmark-3-line text-xs"></i>
                <span>我的收藏</span>
              </Link>
            </div>

            {/* 4. 视图切换按钮 */}
            <div className="flex items-center p-0.5 border border-gray-200 rounded-lg bg-gray-50/50 gap-0.5 ml-auto shrink-0">
              <button
                type="button"
                title="卡片视图"
                onClick={() => setViewMode('card')}
                className={`inline-flex items-center justify-center w-6 h-6 rounded-md transition-all cursor-pointer ${viewMode === 'card'
                  ? 'bg-white text-emerald-600 shadow-2xs font-bold'
                  : 'text-gray-400 hover:text-gray-600'
                  }`}
              >
                <i className="ri-grid-fill text-sm"></i>
              </button>
              <button
                type="button"
                title="列表视图"
                onClick={() => setViewMode('list')}
                className={`inline-flex items-center justify-center w-6 h-6 rounded-md transition-all cursor-pointer ${viewMode === 'list'
                  ? 'bg-white text-emerald-600 shadow-2xs font-bold'
                  : 'text-gray-400 hover:text-gray-600'
                  }`}
              >
                <i className="ri-list-check text-sm"></i>
              </button>
            </div>
          </div>

          {/* 展开的次要筛选条件 */}
          {showAllFilters && (
            <div className="pt-0 pb-1 space-y-2">
              <div className="flex items-center gap-2 overflow-x-auto flex-nowrap">
                <span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap mr-1">课程类型：</span>
                {courseTypes.map((tp) => (
                  <button
                    key={tp}
                    onClick={() => { setSelectedType(tp); setCurrentPage(1); }}
                    className={`text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer ${selectedType === tp
                      ? 'bg-[#e6f7ef] text-[#00c9a7] font-semibold'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                      }`}
                  >
                    {tp}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 overflow-x-auto flex-nowrap">
                <span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap mr-1">课程难度：</span>
                {difficultyOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setSelectedDifficulty(opt.value); setCurrentPage(1); }}
                    className={`text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer ${selectedDifficulty === opt.value
                      ? 'bg-[#e6f7ef] text-[#00c9a7] font-semibold'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                      }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {hasActiveSecondaryFilters && (
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedType('全部');
                      setSelectedDifficulty('all');
                      setSelectedAccessMode('all');
                      setCurrentPage(1);
                    }}
                    className="text-xs text-gray-400 hover:text-emerald-600 cursor-pointer transition-colors inline-flex items-center gap-1"
                  >
                    <i className="ri-refresh-line"></i>
                    重置高级筛选
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Course List */}
      <div id="courses-list" className="site-shell max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-12 w-full flex-1 flex flex-col">
        {loading && courses.length === 0 ? (
          <div className="courses-card-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-gray-100/80 p-4 sm:p-5 animate-pulse flex flex-col justify-between">
                <div className="aspect-[16/9] max-h-[150px] w-full bg-gray-200 rounded-xl mb-3"></div>
                <div className="space-y-2 mb-3">
                  <div className="w-3/4 h-5 bg-gray-200 rounded"></div>
                  <div className="w-1/2 h-4 bg-gray-200 rounded"></div>
                </div>
                <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                  <div className="w-12 h-4 bg-gray-200 rounded"></div>
                  <div className="w-16 h-5 bg-gray-200 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : !loading && courses.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-book-open-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-sm">没有找到匹配的课程</p>
            <button
              onClick={resetFilters}
              className="mt-3 text-sm text-emerald-600 hover:text-emerald-500 cursor-pointer"
            >
              清除所有筛选条件
            </button>
          </div>
        ) : (
          <div className={`transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
            {viewMode === 'card' ? (
              <div className="courses-card-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {courses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {courses.map((course) => (
                  <CourseListItem key={course.id} course={course} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {courses.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            loading={loading}
            className="pt-8"
          />
        )}
      </div>
    </div>
  );
}

const DEFAULT_AUTHORS = [
  '灵搭编剧',
];

function getAuthorName(course: Api.Learning.ResourceResponse): string {
  if ((course as any).author_name) return (course as any).author_name;
  if ((course as any).creator_name) return (course as any).creator_name;
  if ((course as any).author) return (course as any).author;
  const str = course.id || course.title || '';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return DEFAULT_AUTHORS[Math.abs(hash) % DEFAULT_AUTHORS.length];
}

function CourseCard({
  course
}: {
  course: Api.Learning.ResourceResponse;
}) {
  const { rawUser } = useAuth();
  const isUnlimitedPoints = Boolean(
    rawUser?.membership?.subscriptions?.[0]?.benefits_snapshot?.POINT_CONTENT_UNLIMITED
  );

  const assetCache = useAssetRenewStore((state) => state.assetCache);
  const coverAssetId = course.cover_asset?.id || (course as any).cover_asset_id;
  const coverUrl = (coverAssetId && assetCache[coverAssetId]?.url) || course.cover_asset?.access_url || heroIllustration;
  const isVideo = course.resource_type === 'VIDEO' || !course.resource_type;

  const isFree = course.access_mode === 'FREE' || !course.points_price;
  const authorName = getAuthorName(course);
  const lessonCount = course.lesson_count || (isVideo ? 12 : 0);

  const viewCountDisplay = useMemo(() => {
    const count = course.view_count || 0;
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k 人学习`;
    }
    return `${count} 人学习`;
  }, [course.view_count]);

  return (
    <Link
      to={`/courses/${course.id}`}
      className="group relative flex flex-col bg-white rounded-2xl shadow-2xs hover:shadow-lg transition-all duration-300 overflow-hidden hover:-translate-y-1 border border-gray-100/80"
    >
      {/* 封面图容器 */}
      <div className="relative aspect-[16/9] max-h-[150px] w-full overflow-hidden bg-slate-100 shrink-0">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={course.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-white p-4">
            <div className="text-center">
              <i className={`${isVideo ? 'ri-clapperboard-line' : 'ri-book-open-line'} text-2xl text-slate-400 mb-1`}></i>
              <span className="text-xs text-slate-300 font-medium line-clamp-1">{course.title}</span>
            </div>
          </div>
        )}

        {/* 左上角 Badge：视频(紫底) / 文章(橙底) & 已购买 Badge */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 pointer-events-none">
          {isVideo ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#7c4dff] text-white shadow-xs">
              视频
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#ff9800] text-white shadow-xs">
              文章
            </span>
          )}
          {course.is_purchased && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-500 text-white shadow-xs">
              已购买
            </span>
          )}
        </div>

        {/* 右下角 Badge：课节数 */}
        {lessonCount > 0 && (
          <div className="absolute bottom-2.5 right-2.5 pointer-events-none">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-normal bg-black/40 text-white backdrop-blur-xs">
              共 {lessonCount} 节
            </span>
          </div>
        )}
      </div>

      {/* 卡片主体内容 */}
      <div className="flex-1 flex flex-col p-3.5">
        {/* 标题 */}
        <h3 className="text-sm font-bold text-gray-900 leading-snug truncate mb-2 group-hover:text-emerald-600 transition-colors">
          {course.title}
        </h3>

        {/* 作者信息 & 学习人数 */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-2.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <img src={logoImg} alt={authorName} className="w-5 h-5 rounded-full object-cover shrink-0 border border-gray-100" />
            <span className="truncate text-gray-600 text-xs">{authorName}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0 text-gray-400">
            <i className="ri-user-3-line text-xs"></i>
            <span>{viewCountDisplay}</span>
          </div>
        </div>

        {/* 底部价格 / 免费标记 */}
        <div className="flex items-center justify-between mt-auto">
          <div>
            {isFree ? (
              <span className="text-sm font-bold text-[#00c9a7]">免费</span>
            ) : course.is_purchased ? (
              <span className="text-sm font-bold text-[#00c9a7]">已购买</span>
            ) : isUnlimitedPoints ? (
              <span className="text-sm font-bold text-[#00c9a7]">已解锁</span>
            ) : (
              <span className="text-sm font-bold text-[#ff6b00]">
                {course.points_price} <span className="text-xs font-semibold">积分</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function CourseListItem({
  course
}: {
  course: Api.Learning.ResourceResponse;
}) {
  const { rawUser } = useAuth();
  const isUnlimitedPoints = Boolean(
    rawUser?.membership?.subscriptions?.[0]?.benefits_snapshot?.POINT_CONTENT_UNLIMITED
  );
  const getCategoryNameByCode = useLearningCategoryStore((state) => state.getCategoryNameByCode);
  const categoryName = getCategoryNameByCode(course.category_code, '通用课程');

  const assetCache = useAssetRenewStore((state) => state.assetCache);
  const coverAssetId = course.cover_asset?.id || (course as any).cover_asset_id;
  const coverUrl = (coverAssetId && assetCache[coverAssetId]?.url) || course.cover_asset?.access_url || heroIllustration;

  const isFree = course.access_mode === 'FREE' || !course.points_price;
  const studyCount = (course as any).study_count ?? (course as any).view_count ?? 0;
  const lessonText = course.lesson_count ? `${course.lesson_count}节课` : '0节';

  return (
    <Link
      to={`/courses/${course.id}`}
      className="group bg-white rounded-2xl shadow-none hover:shadow-lg hover:shadow-gray-200/80 transition-all duration-300 p-3.5 flex items-center gap-4 hover:-translate-y-0.5"
    >
      {/* Cover */}
      <div className="relative w-48 aspect-[16/9] rounded-xl overflow-hidden shrink-0 hidden sm:block bg-slate-100">
        <img
          src={coverUrl}
          alt={course.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-white/90 backdrop-blur-md text-slate-800 border border-slate-200 shadow-xs">
            {categoryName}
          </span>
          {course.is_purchased && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">
              已购买
            </span>
          )}
          <div className="flex items-center gap-1">
            <img src={logoImg} alt="灵搭资源" className="w-3.5 h-3.5 rounded-full object-cover" />
            <span className="text-xs text-slate-500">灵搭资源</span>
          </div>
        </div>
        <h3 className="font-bold text-slate-800 text-base xl:text-lg transition-colors line-clamp-1">
          {course.title}
        </h3>
        <p className="text-xs text-slate-400 mt-1">{studyCount}人正在学习 · {lessonText}</p>
      </div>

      {/* Stats */}
      <div className="shrink-0 text-right flex flex-col items-end gap-1 pl-4 border-l border-slate-100">
        <div className="font-bold">
          {isFree ? (
            <span className="text-base font-bold text-[#00c9a7]">免费</span>
          ) : course.is_purchased ? (
            <span className="text-base font-bold text-[#00c9a7]">已购买</span>
          ) : isUnlimitedPoints ? (
            <span className="text-base font-bold text-[#00c9a7]">已解锁</span>
          ) : (
            <span className="text-lg font-bold text-[#ff6b00]">
              {course.points_price} <span className="text-xs font-semibold">积分</span>
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
